import { useEffect, useMemo, useState } from 'react';
import { extent, line, scaleLinear } from 'd3';
import { AxisBottom } from './AxisBottom';
import { AxisLeft } from './AxisLeft';
import { useResizeObserver } from './useResizeObserver';
import { useTelemetry } from './useTelemetry';

const MAX_COMPARISONS = 3;

// Slot-indexed palette: same slot = same color across drivers/laps.
const SLOT_COLORS = ['#e10600', '#22d3ee', '#f7f00c', '#a855f7'];

const fmtNum = (v, digits = 0) =>
  v == null || !Number.isFinite(v) ? '—' : v.toFixed(digits);

const formatValue = (channel, v) => {
  if (channel.isBool) return v == null ? '—' : v ? '1' : '0';
  if (v == null || !Number.isFinite(v)) return '—';
  return channel.unit ? `${fmtNum(v, channel.digits)} ${channel.unit}` : fmtNum(v, channel.digits);
};

const formatDelta = (channel, abs) => {
  if (abs == null || !Number.isFinite(abs)) return null;
  return channel.unit ? `${fmtNum(abs, channel.digits)} ${channel.unit}` : fmtNum(abs, channel.digits);
};

const formatSignedSeconds = (s) => {
  if (s == null || !Number.isFinite(s)) return '—';
  const sign = s >= 0 ? '+' : '−';
  return `${sign}${Math.abs(s).toFixed(2)}s`;
};

const CHANNELS = [
  { key: 'speed',    title: 'Speed',    yLabel: 'kph', domain: null,    unit: 'kph', digits: 0 },
  { key: 'throttle', title: 'Throttle', yLabel: '%',   domain: [0, 100], unit: '%',  digits: 0 },
  { key: 'brake',    title: 'Brake',    yLabel: '',    domain: [0, 1],   isBool: true },
  { key: 'gear',     title: 'Gear',     yLabel: '',    domain: [0, 8],   digits: 0 },
  { key: 'rpm',      title: 'RPM',      yLabel: '',    domain: null,    unit: 'rpm', digits: 0 },
];

const formatLapTime = (s) => {
  if (s == null || !Number.isFinite(s)) return '—';
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r.toFixed(3).padStart(6, '0')}`;
};

const TelemetryLoader = ({ race, session, driver, lap, onResult }) => {
  const result = useTelemetry({ race, session, driver, lap });
  useEffect(() => {
    onResult(result);
  }, [result, onResult]);
  return null;
};

const ChannelChart = ({ channel, traces, colors, height, hoveredPos, onHover }) => {
  const { ref, width } = useResizeObserver();
  const margin = { top: 40, right: 12, bottom: 36, left: 56 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const indexed = traces
    .map((t, i) => (t && t.data && t.data[channel.key] ? { ...t, slot: i } : null))
    .filter(Boolean);

  if (innerW <= 0 || innerH <= 0 || indexed.length === 0) {
    return (
      <div
        ref={ref}
        className="rounded-xl border border-f1-border bg-f1-bg/60"
        style={{ height }}
      />
    );
  }

  const maxDistanceM = Math.max(
    ...indexed.map((t) => t.data.distance[t.data.distance.length - 1] || 0),
  );
  const maxKm = maxDistanceM / 1000;
  const xScale = scaleLinear().domain([0, maxKm]).range([0, innerW]).nice();

  let yDomain;
  if (channel.domain) {
    yDomain = channel.domain;
  } else {
    let lo = Infinity;
    let hi = -Infinity;
    for (const t of indexed) {
      const [a, b] = extent(t.data[channel.key]);
      if (a < lo) lo = a;
      if (b > hi) hi = b;
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      return (
        <div
          ref={ref}
          className="rounded-xl border border-f1-border bg-f1-bg/60"
          style={{ height }}
        />
      );
    }
    yDomain = [lo, hi];
  }
  const yScale = scaleLinear().domain(yDomain).range([innerH, 0]).nice();

  const lineGen = line()
    .x((d) => xScale(d[0] / 1000))
    .y((d) => yScale(channel.isBool ? (d[1] ? 1 : 0) : d[1]));

  // Synced tooltip: hovered distance is shared across all charts.
  let hoverInfo = null;
  if (hoveredPos != null) {
    const cursorPxX = xScale(hoveredPos.distanceM / 1000);
    if (cursorPxX >= 0 && cursorPxX <= innerW) {
      const idx = Math.max(0, Math.round(hoveredPos.distanceM / 5));
      const samples = indexed
        .map((t) => {
          const arr = t.data[channel.key];
          if (idx >= arr.length) return null;
          const raw = arr[idx];
          const numeric = channel.isBool ? (raw ? 1 : 0) : raw;
          if (!Number.isFinite(numeric)) return null;
          return {
            slot: t.slot,
            driver: t.driver,
            lap: t.lap,
            raw,
            y: yScale(numeric),
          };
        })
        .filter(Boolean);

      if (samples.length > 0) {
        let delta = null;
        if (
          samples.length === 2 &&
          !channel.isBool &&
          Number.isFinite(samples[0].raw) &&
          Number.isFinite(samples[1].raw)
        ) {
          delta = samples[0].raw - samples[1].raw;
        }
        hoverInfo = {
          kmAt: hoveredPos.distanceM / 1000,
          cursorPxX,
          cursorPxY: hoveredPos.pxY,
          picked: samples,
          delta,
        };
      }
    }
  }

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || x > innerW || y < 0 || y > innerH) {
      onHover(null);
    } else {
      onHover({ distanceM: xScale.invert(x) * 1000, pxY: y });
    }
  };

  // Tooltip follows the cursor; flips to the opposite side when it would overflow.
  const tooltipW = 152;
  const lineH = 12;
  const padY = 4;
  const tooltipH = hoverInfo
    ? padY + 12 + hoverInfo.picked.length * lineH + (hoverInfo.delta != null ? lineH : 0) + padY
    : 0;
  const cursorOffset = 12;
  let tooltipPxX = 0;
  let tooltipPxY = 0;
  if (hoverInfo) {
    // Default to the larger side; only flip when that side overflows.
    const preferRight = hoverInfo.cursorPxX < innerW / 2;
    tooltipPxX = preferRight
      ? hoverInfo.cursorPxX + cursorOffset
      : hoverInfo.cursorPxX - tooltipW - cursorOffset;
    if (preferRight && tooltipPxX + tooltipW > innerW) {
      tooltipPxX = hoverInfo.cursorPxX - tooltipW - cursorOffset;
    } else if (!preferRight && tooltipPxX < 0) {
      tooltipPxX = hoverInfo.cursorPxX + cursorOffset;
    }
    tooltipPxX = Math.max(0, Math.min(innerW - tooltipW, tooltipPxX));
    // Y: clamp only — no flipping.
    tooltipPxY = Math.max(
      0,
      Math.min(innerH - tooltipH, hoverInfo.cursorPxY + cursorOffset),
    );
  }

  // Per-channel y-axis ticks: booleans show only 0/1; everything else scales tick count to plot height.
  let yTicks;
  if (channel.isBool) {
    yTicks = [0, 1];
  } else {
    const targetCount = Math.max(2, Math.floor(innerH / 40));
    yTicks = yScale.ticks(targetCount);
  }

  return (
    <div
      ref={ref}
      className="rounded-xl border border-f1-border bg-f1-bg/60"
      style={{ height }}
    >
    <svg width={width} height={height}>
      <text
        x={margin.left + innerW / 2}
        y={22}
        textAnchor="middle"
        fill="#f2f2f2"
        fontSize={12}
        fontWeight={600}
      >
        {channel.title}
      </text>
      {channel.yLabel && (
        <text
          transform={`translate(14,${margin.top + innerH / 2}) rotate(-90)`}
          textAnchor="middle"
          fill="#8a8a8a"
          fontSize={10}
        >
          {channel.yLabel}
        </text>
      )}
      <text
        x={margin.left + innerW / 2}
        y={height - 6}
        textAnchor="middle"
        fill="#8a8a8a"
        fontSize={10}
      >
        Distance (km)
      </text>
      <g transform={`translate(${margin.left},${margin.top})`}>
        <AxisLeft yScale={yScale} innerWidth={innerW} tickFormat={(v) => v} tickOffset={5} ticks={yTicks} />
        <AxisBottom xScale={xScale} innerHeight={innerH} tickOffset={5} />
        {indexed.map((t) => {
          const arr = t.data[channel.key];
          const points = t.data.distance.map((x, k) => [x, arr[k]]);
          return (
            <path
              key={`${t.slot}-${t.driver}-${t.lap}`}
              d={lineGen(points)}
              fill="none"
              stroke={colors[t.slot]}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
          );
        })}

        {hoverInfo && (
          <g pointerEvents="none">
            <line
              x1={hoverInfo.cursorPxX}
              x2={hoverInfo.cursorPxX}
              y1={0}
              y2={innerH}
              stroke="#8a8a8a"
              strokeDasharray="3,3"
            />
            {hoverInfo.picked.map((p) => (
              <circle
                key={p.slot}
                cx={hoverInfo.cursorPxX}
                cy={p.y}
                r={3}
                fill={colors[p.slot]}
                stroke="#0a0a0a"
                strokeWidth={1}
              />
            ))}
            <rect
              x={tooltipPxX}
              y={tooltipPxY}
              width={tooltipW}
              height={tooltipH}
              rx={4}
              fill="#0a0a0a"
              stroke="#262626"
              opacity={0.95}
            />
            <text
              x={tooltipPxX + 6}
              y={tooltipPxY + padY + 9}
              fill="#8a8a8a"
              fontSize={10}
            >
              {hoverInfo.kmAt.toFixed(2)} km
            </text>
            {hoverInfo.picked.map((p, i) => (
              <text
                key={p.slot}
                x={tooltipPxX + 6}
                y={tooltipPxY + padY + 9 + (i + 1) * lineH}
                fontSize={10}
              >
                <tspan fill={colors[p.slot]}>● </tspan>
                <tspan fill="#f2f2f2">
                  {p.driver} L{p.lap}: {formatValue(channel, p.raw)}
                </tspan>
              </text>
            ))}
            {hoverInfo.delta != null && (
              <text
                x={tooltipPxX + 6}
                y={tooltipPxY + padY + 9 + (hoverInfo.picked.length + 1) * lineH}
                fill="#8a8a8a"
                fontSize={10}
              >
                Δ {formatDelta(channel, Math.abs(hoverInfo.delta))}
              </text>
            )}
          </g>
        )}

        <rect
          x={0}
          y={0}
          width={innerW}
          height={innerH}
          fill="transparent"
          onMouseMove={handleMove}
          onMouseLeave={() => onHover(null)}
        />
      </g>
    </svg>
    </div>
  );
};

const TimeDeltaChart = ({ traces, colors, height, hoveredPos, onHover }) => {
  const { ref, width } = useResizeObserver();
  const margin = { top: 40, right: 12, bottom: 36, left: 56 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const primary = traces[0] && traces[0].data && traces[0].data.time
    ? { ...traces[0], slot: 0 }
    : null;
  const comparisons = traces
    .map((t, i) =>
      i > 0 && t && t.data && t.data.time ? { ...t, slot: i } : null,
    )
    .filter(Boolean);

  if (innerW <= 0 || innerH <= 0) {
    return (
      <div
        ref={ref}
        className="rounded-xl border border-f1-border bg-f1-bg/60"
        style={{ height }}
      />
    );
  }

  if (!primary || comparisons.length === 0) {
    return (
      <div
        ref={ref}
        className="rounded-xl border border-f1-border bg-f1-bg/60 grid place-items-center px-4"
        style={{ height }}
      >
        <p className="text-xs text-f1-muted text-center">
          {primary
            ? 'Add a comparison lap to see time delta'
            : 'Time delta unavailable for this lap'}
        </p>
      </div>
    );
  }

  // Both grids start at 0 with 5m steps, so index alignment matches distance alignment.
  const series = comparisons
    .map((c) => {
      const tPrim = primary.data.time;
      const tCmp = c.data.time;
      const dPrim = primary.data.distance;
      const n = Math.min(
        tPrim.length,
        tCmp.length,
        dPrim.length,
        c.data.distance.length,
      );
      const points = [];
      for (let i = 0; i < n; i++) {
        const tp = tPrim[i];
        const tc = tCmp[i];
        if (Number.isFinite(tp) && Number.isFinite(tc)) {
          points.push([dPrim[i], tc - tp]);
        }
      }
      return { slot: c.slot, driver: c.driver, lap: c.lap, points };
    })
    .filter((s) => s.points.length > 0);

  if (series.length === 0) {
    return (
      <div
        ref={ref}
        className="rounded-xl border border-f1-border bg-f1-bg/60"
        style={{ height }}
      />
    );
  }

  const maxDistanceM = Math.max(
    ...series.map((s) => s.points[s.points.length - 1][0]),
  );
  const maxKm = maxDistanceM / 1000;
  const xScale = scaleLinear().domain([0, maxKm]).range([0, innerW]).nice();

  let absMax = 0;
  for (const s of series) {
    for (const [, dv] of s.points) {
      const a = Math.abs(dv);
      if (a > absMax) absMax = a;
    }
  }
  if (!Number.isFinite(absMax) || absMax < 0.1) absMax = 0.1;
  const yScale = scaleLinear().domain([-absMax, absMax]).range([innerH, 0]).nice();

  const lineGen = line()
    .x((d) => xScale(d[0] / 1000))
    .y((d) => yScale(d[1]));

  let hoverInfo = null;
  if (hoveredPos != null) {
    const cursorPxX = xScale(hoveredPos.distanceM / 1000);
    if (cursorPxX >= 0 && cursorPxX <= innerW) {
      const idx = Math.max(0, Math.round(hoveredPos.distanceM / 5));
      const samples = series
        .map((s) => {
          if (idx >= s.points.length) return null;
          const dv = s.points[idx][1];
          if (!Number.isFinite(dv)) return null;
          return {
            slot: s.slot,
            driver: s.driver,
            lap: s.lap,
            raw: dv,
            y: yScale(dv),
          };
        })
        .filter(Boolean);
      if (samples.length > 0) {
        hoverInfo = {
          kmAt: hoveredPos.distanceM / 1000,
          cursorPxX,
          cursorPxY: hoveredPos.pxY,
          picked: samples,
        };
      }
    }
  }

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || x > innerW || y < 0 || y > innerH) {
      onHover(null);
    } else {
      onHover({ distanceM: xScale.invert(x) * 1000, pxY: y });
    }
  };

  const tooltipW = 152;
  const lineH = 12;
  const padY = 4;
  const tooltipH = hoverInfo
    ? padY + 12 + hoverInfo.picked.length * lineH + padY
    : 0;
  const cursorOffset = 12;
  let tooltipPxX = 0;
  let tooltipPxY = 0;
  if (hoverInfo) {
    const preferRight = hoverInfo.cursorPxX < innerW / 2;
    tooltipPxX = preferRight
      ? hoverInfo.cursorPxX + cursorOffset
      : hoverInfo.cursorPxX - tooltipW - cursorOffset;
    if (preferRight && tooltipPxX + tooltipW > innerW) {
      tooltipPxX = hoverInfo.cursorPxX - tooltipW - cursorOffset;
    } else if (!preferRight && tooltipPxX < 0) {
      tooltipPxX = hoverInfo.cursorPxX + cursorOffset;
    }
    tooltipPxX = Math.max(0, Math.min(innerW - tooltipW, tooltipPxX));
    tooltipPxY = Math.max(
      0,
      Math.min(innerH - tooltipH, hoverInfo.cursorPxY + cursorOffset),
    );
  }

  const targetCount = Math.max(2, Math.floor(innerH / 40));
  const yTicks = yScale.ticks(targetCount);
  const zeroPxY = yScale(0);

  return (
    <div
      ref={ref}
      className="rounded-xl border border-f1-border bg-f1-bg/60"
      style={{ height }}
    >
      <svg width={width} height={height}>
        <text
          x={margin.left + innerW / 2}
          y={22}
          textAnchor="middle"
          fill="#f2f2f2"
          fontSize={12}
          fontWeight={600}
        >
          Time delta vs primary
        </text>
        <text
          transform={`translate(14,${margin.top + innerH / 2}) rotate(-90)`}
          textAnchor="middle"
          fill="#8a8a8a"
          fontSize={10}
        >
          Δ s
        </text>
        <text
          x={margin.left + innerW / 2}
          y={height - 6}
          textAnchor="middle"
          fill="#8a8a8a"
          fontSize={10}
        >
          Distance (km)
        </text>
        <g transform={`translate(${margin.left},${margin.top})`}>
          <AxisLeft
            yScale={yScale}
            innerWidth={innerW}
            tickFormat={(v) => (v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2))}
            tickOffset={5}
            ticks={yTicks}
          />
          <AxisBottom xScale={xScale} innerHeight={innerH} tickOffset={5} />
          {Number.isFinite(zeroPxY) && (
            <line
              x1={0}
              x2={innerW}
              y1={zeroPxY}
              y2={zeroPxY}
              stroke="#3a3a3a"
              strokeWidth={1}
            />
          )}
          {series.map((s) => (
            <path
              key={`${s.slot}-${s.driver}-${s.lap}`}
              d={lineGen(s.points)}
              fill="none"
              stroke={colors[s.slot]}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
          ))}

          {hoverInfo && (
            <g pointerEvents="none">
              <line
                x1={hoverInfo.cursorPxX}
                x2={hoverInfo.cursorPxX}
                y1={0}
                y2={innerH}
                stroke="#8a8a8a"
                strokeDasharray="3,3"
              />
              {hoverInfo.picked.map((p) => (
                <circle
                  key={p.slot}
                  cx={hoverInfo.cursorPxX}
                  cy={p.y}
                  r={3}
                  fill={colors[p.slot]}
                  stroke="#0a0a0a"
                  strokeWidth={1}
                />
              ))}
              <rect
                x={tooltipPxX}
                y={tooltipPxY}
                width={tooltipW}
                height={tooltipH}
                rx={4}
                fill="#0a0a0a"
                stroke="#262626"
                opacity={0.95}
              />
              <text
                x={tooltipPxX + 6}
                y={tooltipPxY + padY + 9}
                fill="#8a8a8a"
                fontSize={10}
              >
                {hoverInfo.kmAt.toFixed(2)} km
              </text>
              {hoverInfo.picked.map((p, i) => (
                <text
                  key={p.slot}
                  x={tooltipPxX + 6}
                  y={tooltipPxY + padY + 9 + (i + 1) * lineH}
                  fontSize={10}
                >
                  <tspan fill={colors[p.slot]}>● </tspan>
                  <tspan fill="#f2f2f2">
                    {p.driver} L{p.lap}: {formatSignedSeconds(p.raw)}
                  </tspan>
                </text>
              ))}
            </g>
          )}

          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={handleMove}
            onMouseLeave={() => onHover(null)}
          />
        </g>
      </svg>
    </div>
  );
};

export const TelemetryOverlay = ({ open, lap, race, session, drivers = [], onClose, onSignInClick }) => {
  const [comparisons, setComparisons] = useState([]);
  const [pickerDriver, setPickerDriver] = useState('');
  const [pickerLap, setPickerLap] = useState('');
  const [results, setResults] = useState({});
  const [hoveredPos, setHoveredPos] = useState(null);

  useEffect(() => {
    if (!open) {
      setComparisons([]);
      setPickerDriver('');
      setPickerLap('');
      setResults({});
      setHoveredPos(null);
    }
  }, [open]);

  const slots = useMemo(() => {
    if (!lap) return [];
    return [
      { key: `${lap.driver}-${lap.lapNumber}`, driver: lap.driver, lap: lap.lapNumber, isPrimary: true },
      ...comparisons.map((c) => ({
        key: `${c.driver}-${c.lap}`,
        driver: c.driver,
        lap: c.lap,
        isPrimary: false,
      })),
    ];
  }, [lap, comparisons]);

  const colors = useMemo(
    () => slots.map((_, i) => SLOT_COLORS[i % SLOT_COLORS.length]),
    [slots],
  );

  const traces = slots.map((s) => {
    const r = results[s.key];
    return r && r.status === 'ready' ? { driver: s.driver, lap: s.lap, data: r.data } : null;
  });

  const primaryStatus = lap ? results[`${lap.driver}-${lap.lapNumber}`]?.status || 'loading' : 'idle';

  const handleAddComparison = () => {
    if (!pickerDriver || !pickerLap) return;
    const lapNum = Number.parseInt(pickerLap, 10);
    if (!Number.isInteger(lapNum) || lapNum <= 0) return;
    if (comparisons.length >= MAX_COMPARISONS) return;
    if (
      lap &&
      pickerDriver === lap.driver &&
      lapNum === lap.lapNumber
    )
      return;
    if (comparisons.some((c) => c.driver === pickerDriver && c.lap === lapNum)) return;
    setComparisons([...comparisons, { driver: pickerDriver, lap: lapNum }]);
    setPickerLap('');
  };

  const handleRemove = (idx) => {
    const c = comparisons[idx];
    setComparisons(comparisons.filter((_, i) => i !== idx));
    setResults((prev) => {
      const next = { ...prev };
      delete next[`${c.driver}-${c.lap}`];
      return next;
    });
  };

  if (!open || !lap) return null;

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-f1-border bg-f1-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 rounded-full text-f1-muted hover:text-f1-text grid place-items-center"
          aria-label="Close"
        >
          ×
        </button>

        <header className="mb-4">
          <h2 className="text-lg font-semibold tracking-tight text-f1-text">
            Lap telemetry · {lap.driver} · Lap {lap.lapNumber}
          </h2>
          <p className="text-xs text-f1-muted mt-0.5">
            {session} · {formatLapTime(lap.lapTimeSeconds)} · compare up to {MAX_COMPARISONS} drivers
          </p>
        </header>

        {/* Hidden fetchers — one per slot. Each child calls useTelemetry. */}
        {slots.map((s) => (
          <TelemetryLoader
            key={s.key}
            race={race}
            session={session}
            driver={s.driver}
            lap={s.lap}
            onResult={(r) =>
              setResults((prev) =>
                prev[s.key] === r ? prev : { ...prev, [s.key]: r }
              )
            }
          />
        ))}

        {primaryStatus === 'unauthenticated' ? (
          <div className="rounded-xl border border-f1-border bg-f1-bg/60 px-6 py-12 grid place-items-center">
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-f1-muted text-center max-w-[24rem]">
                Sign in to view telemetry traces.
              </p>
              <button
                onClick={() => {
                  onClose?.();
                  onSignInClick?.();
                }}
                className="text-sm rounded-md bg-f1-red px-3.5 py-2 font-semibold hover:opacity-90 transition-opacity"
              >
                Sign in
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-4 pb-3 border-b border-f1-border">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider text-f1-muted">Driver</label>
                <select
                  value={pickerDriver}
                  onChange={(e) => setPickerDriver(e.target.value)}
                  className="bg-f1-bg border border-f1-border rounded px-2 py-1 text-sm"
                >
                  <option value="">—</option>
                  {drivers.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider text-f1-muted">Lap</label>
                <input
                  type="number"
                  min={1}
                  value={pickerLap}
                  onChange={(e) => setPickerLap(e.target.value)}
                  className="bg-f1-bg border border-f1-border rounded px-2 py-1 text-sm w-24"
                />
              </div>
              <button
                onClick={handleAddComparison}
                disabled={!pickerDriver || !pickerLap || comparisons.length >= MAX_COMPARISONS}
                className="text-xs rounded-md bg-f1-red px-3 py-1.5 font-semibold disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                Add comparison
              </button>

              <div className="flex flex-wrap items-center gap-2 ml-auto">
                {slots.map((s, i) => (
                  <span
                    key={s.key}
                    className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-f1-border bg-f1-bg"
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: colors[i] }}
                    />
                    {s.driver} L{s.lap}
                    {!s.isPrimary && (
                      <button
                        onClick={() => handleRemove(i - 1)}
                        className="text-f1-muted hover:text-f1-text ml-1"
                        aria-label={`Remove ${s.driver} L${s.lap}`}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CHANNELS.map((c) => (
                <ChannelChart
                  key={c.key}
                  channel={c}
                  traces={traces}
                  colors={colors}
                  height={192}
                  hoveredPos={hoveredPos}
                  onHover={setHoveredPos}
                />
              ))}
              <TimeDeltaChart
                traces={traces}
                colors={colors}
                height={192}
                hoveredPos={hoveredPos}
                onHover={setHoveredPos}
              />
            </div>

            {primaryStatus === 'loading' && (
              <p className="text-xs text-f1-muted mt-3">Loading telemetry…</p>
            )}
            {primaryStatus === 'not_found' && (
              <p className="text-xs text-f1-muted mt-3">
                No telemetry recorded for this lap (often pit-in/pit-out laps).
              </p>
            )}
            {primaryStatus === 'error' && (
              <p className="text-xs text-f1-red mt-3">Failed to load telemetry.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
